import { IComment, ICommentCreate, ICommentCreated } from "@defs/comment";
import {
  ITask,
  ITaskCompleted,
  ITaskHandle,
  ITaskReopenResponse,
  ITaskUpdateExpiration,
} from "@defs/task";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { PayloadActionFulfilledMeta } from "@defs/types";
import commentsStateHelper from "@helpers/comments-state-helper";

interface TaskState {
  loading: boolean;
  data: ITask;
  error: boolean;
}

const initialState: TaskState = {
  loading: true,
  data: null,
  error: false,
};

const taskSlice = createSlice({
  name: "task",
  initialState,
  reducers: {
    reset(state) {
      state.data = initialState.data;
      state.loading = initialState.loading;
    },
  },
  extraReducers: {
    "task/detail/pending": (state) => {
      state.loading = true;
      state.error = false;
    },

    "task/detail/fulfilled": (state, action: PayloadAction<ITask>) => {
      state.loading = false;
      state.data = action.payload;
    },

    "task/detail/rejected": (state) => {
      state.loading = false;
      state.error = true;
    },

    "comment/delete/fulfilled": (state, action: PayloadAction<IComment>) => {
      if (state.data) {
        const comment = action.payload;

        state.data.comments = commentsStateHelper.updateCommentsAfterDelete(
          state.data.comments,
          comment
        );
      }
    },

    "documents/delete/fulfilled": (state, action: PayloadAction<string>) => {
      if (state.data) {
        const comments = commentsStateHelper.removeDocumentFromComment(
          state.data.comments,
          action.payload
        );

        state.data.comments = comments;
      }
    },

    "comment/create/fulfilled": (
      state,
      action: PayloadAction<
        ICommentCreated,
        string,
        PayloadActionFulfilledMeta<ICommentCreate>
      >
    ) => {
      const { comment } = action.payload;
      const { task } = action.meta.arg;

      if (state.data && state.data.id === task) {
        state.data.comments.push(comment);
      }
    },

    "tasks/complete/fulfilled": (
      state,
      action: PayloadAction<ITaskCompleted>
    ) => {
      const { task } = action.payload;

      if (state.data && state.data.id === task.id) {
        state.data.handler = task.handler;
        state.data.handled_at = task.handled_at;
        state.data.completed_at = task.completed_at;
      }
    },

    "tasks/handle/fulfilled": (
      state,
      action: PayloadAction<
        ITaskHandle,
        string,
        PayloadActionFulfilledMeta<string>
      >
    ) => {
      const id = action.meta.arg;

      if (state.data && state.data.id === id) {
        const { handler, handled_at } = action.payload;

        state.data.handler = handler;
        state.data.handled_at = handled_at;
      }
    },

    "tasks/update/fulfilled": (state, action: PayloadAction<ITask>) => {
      const task = action.payload;
      if (state.data && state.data.id === task.id) {
        const { handler, group, title, description, expires_at } =
          action.payload;

        state.data.handler = handler;
        state.data.group = group;
        state.data.title = title;
        state.data.description = description;
        state.data.expires_at = expires_at;
      }
    },

    "task/reopen/fulfilled": (
      state,
      action: PayloadAction<
        ITaskReopenResponse,
        string,
        PayloadActionFulfilledMeta<string>
      >
    ) => {
      const id = action.meta.arg;

      if (state.data && state.data.id === id) {
        state.data.completed_at = null;
      }
    },

    "task/expiration/update/fulfilled": (
      state,
      action: PayloadAction<
        string,
        string,
        PayloadActionFulfilledMeta<ITaskUpdateExpiration>
      >
    ) => {
      if (state.data) {
        const { id } = action.meta.arg;
        const expires_at = action.payload;

        if (state.data.id === id) {
          state.data.expires_at = expires_at;
        }
      }
    },
  },
});

export const taskActions = taskSlice.actions;

export default taskSlice.reducer;
